import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { PerimeterType } from '@atlaskit/feature-gate-js-client/types';
import FeatureGateSingleFetchProvider from '@atlaskit/feature-gate-single-fetch-provider/provider';
import { ConfigClient } from '@atlaskit/web-config-client/main';

// eslint-disable-next-line no-restricted-imports
import { isFedRamp } from '@atlaskit/atlassian-context/is-fedramp';

import type { SessionDataType } from '@confluence/session-data/entry-points/SessionDataTypes.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { applyPersistedStatsigOverrides } from '@confluence/session-data/entry-points/persistedStatsigOverrides.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { buildKeyToSemVerString } from '@confluence/ssr-utilities/entry-points/buildKey.ts';
import { getBuildInfo } from '@confluence/build-info';

import { getStatsigEnvironment } from './getStatsigEnvironment.ts';
import { getFeatureFlagsServiceApiKey } from './getFeatureFlagsServiceApiKey.ts';
import { getSourceCustomAttributes } from './getSourceCustomAttributes.ts';

export type StatsigOptions = {
	userId: SessionDataType['userId'];
	cloudId: SessionDataType['cloudId'] | null;
	environment: SessionDataType['environment'] | string;
	orgId: SessionDataType['orgId'] | null;
};

export async function initializeClientWithProvider({
	cloudId,
	environment,
	userId,
	orgId,
}: StatsigOptions): Promise<void> {
	// https://bitbucket.org/atlassian/feature-flag-service/src/a6adccc26a95818c8128c63e4eb817414793b3c2/src/main/resources/application.yml#lines-386
	const TARGET_APP = 'confluence_web';

	try {
		const provider = new FeatureGateSingleFetchProvider({
			apiKey: getFeatureFlagsServiceApiKey(environment),
		});

		let appVersion;
		try {
			const frontendVersion = getBuildInfo().FRONTEND_VERSION;
			appVersion = buildKeyToSemVerString(frontendVersion);
		} catch (e) {
			// failed to get appVersion, do nothing
		}

		const [configClientInstance] = await Promise.all([
			ConfigClient.fetch({
				// Base URL of the feature-flag-service
				ffsBaseUrl: '/gateway/api/flags',
				ffsApiKey: getFeatureFlagsServiceApiKey(environment),
				// User context to evaluate the configuration against
				context: {
					namespace: TARGET_APP,
					identifiers: {
						atlassianAccountId: userId || undefined,
						tenantId: cloudId || undefined,
						atlassianOrgId: orgId || undefined,
					},
					metadata: {},
				},
			}),
			FeatureGates.initializeWithProvider(
				{
					environment: getStatsigEnvironment(environment),
					targetApp: TARGET_APP,
					perimeter: isFedRamp() ? PerimeterType.FEDRAMP_MODERATE : PerimeterType.COMMERCIAL,
				},
				provider,
				{
					atlassianAccountId: userId || undefined,
					tenantId: cloudId || undefined,
					atlassianOrgId: orgId || undefined,
				},
				{
					...(appVersion ? { appVersion } : {}),
					...getSourceCustomAttributes(),
				},
			),
		]);

		// StatsigConfigurations reads FeatureGates this must be initialized after FeatureGates
		StatsigConfigurations.initialize(configClientInstance);
		if (expValEquals('confluence_apply_statsig_overrides_on_client_init', 'isEnabled', true)) {
			applyPersistedStatsigOverrides();
		}
	} catch (error: unknown) {
		if (error instanceof Error) {
			getMonitoringClient().submitError(error, {
				attribution: 'statsig',
			});
		}
	}
}
